'use client'

import { File } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { ITranslationKey, T } from '@/components/t'
import { ICommonProps } from '@/features/common/common-props'
import { homeSizeStyles } from '@/features/home/utils/home-size'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

interface ICommonBenefit {
    title: string
    description: string | string[]
    icon?: string
    pdf?: {
        label: string
        src: string
    }
}

export const CommonBenefits = ({
    tBaseKey,
    itemsContainerClassName,
    titleClassName,
}: ICommonProps & { itemsContainerClassName?: string; titleClassName?: string }) => {
    const t = useTranslations()

    const items = useMemo<ICommonBenefit[]>(() => {
        try {
            const rawItems = t.raw((tBaseKey + '.items') as ITranslationKey)

            // biome-ignore lint/suspicious/noExplicitAny: biome migration
            return rawItems.map((item: any) => {
                return {
                    title: item.title,
                    description: item.description,
                    icon: item.icon,
                    pdf: item.pdf,
                }
            })
        } catch (error) {
            // biome-ignore lint/suspicious/noConsole: intentional error logging
            console.log('error', error)
            return null
        }
    }, [t, tBaseKey])

    if (!items?.length) return null

    return (
        <div
            className={cn(
                'bg-[#53F293]/5 py-12 md:py-[96px] w-full flex items-center justify-center'
            )}
        >
            <div className={cn('flex flex-col items-center', homeSizeStyles)}>
                <T
                    h2
                    t={`${tBaseKey}.title` as ITranslationKey}
                    className={cn(
                        'leading-[1.5] font-bold text-[32px] md:text-[32px] mb-8 md:mb-12 text-left md:text-left px-4 md-px-0',
                        titleClassName
                    )}
                />

                <div
                    className={cn(
                        `grid grid-cols-2 ${items.length > 4 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-x-0 md:gap-x-16
                        gap-y-8 md:gap-y-12`,
                        itemsContainerClassName
                    )}
                >
                    {items.map((item, _i) => {
                        const imageSrc = item.icon

                        if (!imageSrc) return null

                        return (
                            <div
                                key={'client-become-a-partner-benefits' + item.title}
                                className="px-[20px] md:px-4 w-full"
                            >
                                <Image
                                    priority
                                    alt="Small illustration"
                                    src={imageSrc}
                                    height={40}
                                    width={40}
                                    className="mb-4 md:h-10 md:w-10 h-8 w-8"
                                />

                                <h2 className="mb-1">{item.title}</h2>

                                {run(() => {
                                    if (typeof item.description === 'string') {
                                        return (
                                            <p className="text-gray-500 text-[14px] md:text-[16px]">
                                                {item.description}
                                            </p>
                                        )
                                    }

                                    return (
                                        <ul className="list-disc">
                                            {item.description.map((bullet) => {
                                                return (
                                                    <li
                                                        className="text-gray-500 text-[14px] md:text-[16px] mb-2"
                                                        key={bullet}
                                                    >
                                                        {bullet}
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    )
                                })}

                                {item.pdf && (
                                    <a
                                        href={item.pdf.src}
                                        target="_blank"
                                        className="px-4 py-2 rounded-s bg-neotaste flex gap-2 items-center mt-4 group hover:bg-black/10 transition-all
                                            cursor-pointer w-fit"
                                        rel="noreferrer"
                                    >
                                        <File className="w-5 h-5" />

                                        <span className="text-[14px] md:text-[16px] underline font-medium">
                                            {item.pdf.label}
                                        </span>
                                    </a>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
